import { Component, EventEmitter, Output } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { DataImportsService } from '../_services/data-imports.service';
import { FilesService } from 'src/app/modules/common/_services/files.service';
import { File as FileResource } from 'src/app/modules/common/_types/file';

@Component({
    selector: 'app-imports-create',
    templateUrl: './imports-create.component.html',
    styleUrls: ['./imports-create.component.scss'],
    standalone: false
})
export class ImportsCreateComponent {
    @Output() created = new EventEmitter<void>();
    @Output() cancelled = new EventEmitter<void>();

    importType: number | null = null;
    mode = 'Crear';
    stopOnFirstError = false;
    // Se arranca en modo prueba a proposito: primero se valida el fichero y despues
    // se promociona la importacion de verdad desde su ficha.
    dryRun = true;
    allowDuplicates = false;
    truncateValues = false;
    file: File | null = null;
    submitting = false;
    templateErrors: { row: number; column?: string; message: string }[] = [];
    showErrorsDialog = false;

    types = [
        { label: 'Expedientes', value: 0 },
        { label: 'Incidencias', value: 1 },
        { label: 'Notas', value: 2 },
        { label: 'Operarios', value: 3 },
        { label: 'Trabajos', value: 4 },
        { label: 'Visitas', value: 5 }
    ];

    /**
     * Regla con la que cada tipo decide que una fila ya existe. Se muestra al elegir el
     * tipo para que se sepa que se va a comprobar antes de lanzar la importacion.
     */
    uniquenessRules: Record<number, string> = {
        0: 'Se considera el mismo expediente si coinciden número, contrato y compañía.'
    };

    modes = [
        { label: 'Crear', value: 'Crear' },
        { label: 'Crear y actualizar', value: 'CrearActualizar' }
    ];

    /** Regla del tipo elegido, o el aviso de que ese tipo no comprueba duplicados. */
    get uniquenessRule(): string | null {
        if (this.importType === null) return null;
        return this.uniquenessRules[this.importType] ?? 'Este tipo no comprueba duplicados: cada fila se da de alta.';
    }

    /** Solo tiene sentido comprobar duplicados en los tipos que tienen regla. */
    get checksDuplicates(): boolean {
        return this.importType !== null && this.uniquenessRules[this.importType] != null;
    }

    constructor(private service: DataImportsService, private filesService: FilesService) {}

    onFileSelect(event: any): void {
        this.file = event.files?.[0] ?? null;
    }

    async submit(): Promise<void> {
        if (this.importType === null || !this.file) return;
        this.submitting = true;
        let fileId: string | null = null;
        try {
            fileId = await this.uploadFile(this.file);
            this.service.validate({
                fileId,
                importType: this.importType,
                mode: this.mode,
                stopOnFirstError: this.stopOnFirstError,
                dryRun: this.dryRun,
                allowDuplicates: this.allowDuplicates,
                truncateValues: this.truncateValues
            }).subscribe({
                next: () => { this.submitting = false; this.created.emit(); },
                error: (err) => {
                    this.submitting = false;
                    const errs = err?.error?.errors;
                    if (Array.isArray(errs)) {
                        this.templateErrors = errs;
                        this.showErrorsDialog = true;
                    } else {
                        alert('Error inesperado: ' + (err?.message ?? 'sin detalle'));
                    }
                    if (fileId) this.deleteFile(fileId);
                }
            });
        } catch (e: any) {
            this.submitting = false;
            alert('Error subiendo el fichero: ' + (e?.message ?? e));
        }
    }

    private async uploadFile(file: File): Promise<string> {
        const data = new FileResource();
        data.fileName = file.name;
        data.size = file.size;
        data.contentType = file.type || 'application/octet-stream';

        const fileResponse = await firstValueFrom(this.filesService.save(data));
        const id = fileResponse.id as string;
        if (!id) throw new Error('No se obtuvo id del fichero.');

        await firstValueFrom(this.filesService.upload(id, file));
        return id;
    }

    private deleteFile(id: string): void {
        this.filesService.delete(id).subscribe({ error: () => {} });
    }

    cancel(): void {
        this.cancelled.emit();
    }
}
